import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import * as maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Address, CreateAddressParams } from '../../core/models/address.model';
import {
  MAP_STYLE_ORDER,
  MapStyleId,
  STREETS_URL,
  buildHybridStyle,
  buildSatelliteStyle,
} from '../../core/models/basemap.model';
import { Interaction } from '../../core/models/interaction.model';
import { CHIP_COLOR_BG_VAR, outcomeStyle } from '../../core/models/outcome-style.model';
import { Region } from '../../core/models/region.model';
import { AddressService } from '../../core/services/address.service';
import { AuthService } from '../../core/services/auth.service';
import { ClockSyncService } from '../../core/services/clock-sync.service';
import { GeocodingService } from '../../core/services/geocoding.service';
import { InteractionService } from '../../core/services/interaction.service';
import { LoggerService } from '../../core/services/logger.service';
import { MapDisplaySettingsService } from '../../core/services/map-display-settings.service';
import { MapModeService } from '../../core/services/map-mode.service';
import { MapZoomService } from '../../core/services/map-zoom.service';
import { RegionsService } from '../../core/services/regions.service';
import { RegistryLookupService, RegistryUnit } from '../../core/services/registry-lookup.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { OutcomeChipComponent } from '../../shared/components/outcome-chip/outcome-chip.component';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/** Fallback initial camera when there's no saved view and (unlike a single-region deployment)
 *  no one bbox to fit to anymore -- see map.component.ts's multi-region design note below.
 *  Centering on the user's real location instead is better, deliberately deferred until the
 *  map has its own live location indicator/control to hang that logic off of. */
const NORTH_AMERICA_VIEW = { center: [-98, 45] as [number, number], zoom: 3.2 };

type DateRangeKind = 'today' | 'last24h' | 'week' | 'last7d' | 'month' | 'last30d' | 'custom';

interface ActivityDateRangeState {
  range: DateRangeKind;
  from: string;
  to: string;
}

const MAP_VIEW_KEY = 'map-view';
/** Remembers the user's last activity-filter choice across sessions; 'last7d' is only the
 *  first-ever-use default (see dateModel init below), not applied on every load. */
const ACTIVITY_DATE_RANGE_KEY = 'map-activity-date-range';

// Duplicated from backend/src/registry/generate-tiles.ts's COVERAGE_GRID_DEGREES -- not
// shared at runtime (see this file's addRegistryLayers comment on `promoteId: 'cell_id'`
// and the sidecar-tile-math precedent for why: @app/shared is types-only). Must stay in
// sync with the backend value or reloadOwnActivity's computed cell ids won't match the
// coverage tiles' real `cell_id` property.
const COVERAGE_GRID_DEGREES = 0.01;

/** Same bucketing the coverage layer's tiles were built with (see backend's
 *  buildCoverageFeatureCollection) -- lets reloadOwnActivity mark a coverage cell's
 *  feature-state color from a plain lat/lng, the same way it already does per building_id. */
function coverageCellId(lng: number, lat: number): string {
  return `${Math.floor(lng / COVERAGE_GRID_DEGREES)}_${Math.floor(lat / COVERAGE_GRID_DEGREES)}`;
}

// The zoom a tap on a coalesced/coverage group always ends up at or past -- matches the
// buildings layer's own minzoom (see generate-tiles.ts) so "zoom in on this group" actually
// reaches individual buildings, not just a slightly closer view of the same coarse shapes.
const TAP_MIN_ZOOM = 15;

/** Persisted per user so a refresh lands on the same map style, layers, and camera. */
interface MapViewState {
  mapStyleId: MapStyleId;
  activityLayerVisible: boolean;
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

/** Mounts an Angular-rendered element into MapLibre's control stack so it sits with the built-ins. */
class ElementControl implements maplibregl.IControl {
  constructor(private el: HTMLElement) {}
  onAdd(): HTMLElement {
    return this.el;
  }
  onRemove(): void {
    this.el.remove();
  }
}

/** One row in the "which unit is this" picker sheet for a multi-unit building. */
interface UnitPickerRow {
  registryUnit: RegistryUnit;
  localAddress: Address | null;
}

/** Basemap POI tap state -- the OpenMapTiles `poi` source-layer only carries a name and a
 *  class/subclass pair (no address/phone/hours), so that's all there is to show. */
interface PoiPopupInfo {
  name: string | null;
  poiClass: string | null;
  subclass: string | null;
  point: maplibregl.Point;
}

/** Maps a handful of common OpenMapTiles POI `class` values to a Lucide icon name for
 *  `<app-icon>`. Deliberately not exhaustive -- unmapped classes fall back to 'map-pin'. */
const POI_CLASS_ICONS: Record<string, string> = {
  restaurant: 'utensils',
  cafe: 'coffee',
  bar: 'beer',
  fast_food: 'utensils',
  shop: 'shopping-bag',
  lodging: 'bed',
  hospital: 'cross',
  doctors: 'cross',
  pharmacy: 'cross',
  school: 'graduation-cap',
  college: 'graduation-cap',
  bank: 'landmark',
  fuel: 'fuel',
  park: 'trees',
  bus: 'bus',
  railway: 'train-front',
};

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    ClickOutsideDirective,
    IconComponent,
    ModalDialogComponent,
    OutcomeChipComponent,
    RouterOutlet,
    TippyDirective,
    TranslatePipe,
  ],
  templateUrl: './map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './map.component.css',
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  private addressService = inject(AddressService);
  private authService = inject(AuthService);
  private clockSync = inject(ClockSyncService);
  private geocodingService = inject(GeocodingService);
  private interactionService = inject(InteractionService);
  private displaySettings = inject(MapDisplaySettingsService);
  private mapModeService = inject(MapModeService);
  private mapZoomService = inject(MapZoomService);
  private regionsService = inject(RegionsService);
  private registryLookup = inject(RegistryLookupService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);
  private log = inject(LoggerService).getLogger('map');

  @ViewChild('mapEl') private mapEl!: ElementRef<HTMLDivElement>;
  @ViewChild('actionsCtrl') private actionsCtrl!: ElementRef<HTMLDivElement>;
  @ViewChild('styleCtrl') private styleCtrl!: ElementRef<HTMLDivElement>;
  @ViewChild('layersCtrl') private layersCtrl!: ElementRef<HTMLDivElement>;

  private map!: maplibregl.Map;
  private darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

  private savedView = this.userStorage.getTypedItem<MapViewState>(MAP_VIEW_KEY);

  /** Every `ready` catalog region, fetched once and reused across every style.load (basemap
   *  switches rebuild the registry overlay -- see addRegistryLayers) rather than refetched
   *  per rebuild. Registry data is shared across all orgs, so this isn't org-scoped. */
  private readyRegions: Promise<Region[]> = firstValueFrom(this.regionsService.getRegions()).then(
    (res) => res.regions.filter((r) => r.status === 'ready'),
  );

  mapStyles = MAP_STYLE_ORDER;
  mapStyleId = signal<MapStyleId>(this.savedView?.mapStyleId ?? 'streets');
  showStylePicker = signal(false);

  /** What clicking/tapping the style toggle switches to next -- shown as the button's own
   *  preview swatch, matching Google Maps' "the thumbnail is what you'll get" convention. */
  nextStyleId = computed<MapStyleId>(() => {
    const i = MAP_STYLE_ORDER.indexOf(this.mapStyleId());
    return MAP_STYLE_ORDER[(i + 1) % MAP_STYLE_ORDER.length];
  });

  layersOpen = signal(false);
  activityLayerVisible = signal<boolean>(this.savedView?.activityLayerVisible ?? true);

  searchQuery = signal('');
  searchResults = signal<Array<{ address: string; lat: number; lng: number }>>([]);
  searchLoading = signal(false);
  showSearchResults = signal(false);

  /** The unconfirmed marker a selected search result drops -- see showSearchPin/confirmSearchPin.
   *  Null when no search pin is currently on the map. */
  private searchPinMarker: maplibregl.Marker | null = null;

  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private suppressNextStyleClick = false;

  dropPinMode = signal(false);
  loadingTap = signal(false);

  activityFilterOpen = signal(false);
  /** Plain-text rendering of the current activity-filter range, overlaid on the map -- kept as
   *  its own signal (rather than a computed) since dateModel is a plain object mutated in place
   *  by Formly, not a signal itself; see updateActiveRangeLabel. */
  activeRangeLabel = signal('');

  /** Layer ids for the basemap's own POI symbol layers, recomputed on every style.load since
   *  they belong to the fetched vendor style, not something this app defines -- kept by
   *  reference in the map.on('click', poiLayerIds, ...) binding below (see onPoiTap). */
  private poiLayerIds: string[] = [];
  selectedPoi = signal<PoiPopupInfo | null>(null);

  showUnitPicker = signal(false);
  pickerRows = signal<UnitPickerRow[]>([]);
  pickerBuildingId = signal<string | null>(null);

  /** The address whose detail/edit/interaction screen is currently docked over the map (derived
   *  from the ':id' param on whichever /map/addresses/:id/... child route is active -- see
   *  updateSelectedAddressFromRoute). Null when no such route is active (map, address list, or
   *  the "new address" form, which has no address yet to highlight). */
  selectedAddressId = signal<string | null>(null);
  private selectedBuildingId: string | null = null;
  private routerEventsSub?: Subscription;

  /** Addresses with no registry building match -- dropped pins (manual or search-resolved)
   *  and rural addresses. Reactive so a freshly search-resolved or pin-dropped address
   *  appears on the map immediately, without waiting on a route change or filter tweak to
   *  trigger a reload (see design doc §9.5 "Pins"). */
  private footprintlessAddresses = this.addressService.createFootprintlessQuery().data;
  /** Keeps the 'pins' GeoJSON source in sync with footprintlessAddresses -- runs once at
   *  construction (before the map exists; syncPinsSource no-ops until addPinsLayer has run)
   *  and again on every subsequent change to the query result. */
  private syncPinsEffect = effect(() => this.syncPinsSource(this.footprintlessAddresses()));

  dateForm = new FormGroup({});
  dateModel: ActivityDateRangeState = this.userStorage.getTypedItem<ActivityDateRangeState>(
    ACTIVITY_DATE_RANGE_KEY,
  ) ?? { range: 'last7d', from: '', to: '' };
  dateFields: FormlyFieldConfig[] = [];

  ngOnInit(): void {
    this.buildDateFields();
    this.updateActiveRangeLabel();

    this.routerEventsSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) this.onNavigationEnd();
    });
    this.updateSelectedAddressFromRoute();
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchPinMarker?.remove();
    this.map?.remove();
  }

  private buildDateFields(): void {
    const t = (key: string) => this.translate.translate(key);
    this.dateFields = [
      {
        key: 'range',
        type: 'native-select',
        props: {
          label: t('map.dateFilter.label'),
          showLabel: true,
          options: [
            { value: 'today', label: t('map.dateFilter.today') },
            { value: 'last24h', label: t('map.dateFilter.last24Hours') },
            { value: 'week', label: t('map.dateFilter.thisWeek') },
            { value: 'last7d', label: t('map.dateFilter.last7Days') },
            { value: 'month', label: t('map.dateFilter.thisMonth') },
            { value: 'last30d', label: t('map.dateFilter.last30Days') },
            { value: 'custom', label: t('map.dateFilter.custom') },
          ],
        },
      },
      {
        key: 'from',
        type: 'datetime',
        props: { label: t('map.dateFilter.from'), attributes: { 'data-testid': 'map-date-from' } },
        expressions: { hide: (field) => field.model?.range !== 'custom' },
      },
      {
        key: 'to',
        type: 'datetime',
        props: { label: t('map.dateFilter.to'), attributes: { 'data-testid': 'map-date-to' } },
        expressions: { hide: (field) => field.model?.range !== 'custom' },
      },
    ];
    this.dateForm.valueChanges.subscribe(() => {
      this.userStorage.setTypedItem<ActivityDateRangeState>(
        ACTIVITY_DATE_RANGE_KEY,
        this.dateModel,
      );
      this.updateActiveRangeLabel();
      this.reloadOwnActivity();
    });
  }

  /** Renders dateModel as the plain-text label overlaid on the map -- called on init and on
   *  every form change rather than as a computed(), since dateModel is a plain object Formly
   *  mutates in place, not a signal. */
  private updateActiveRangeLabel(): void {
    const t = (key: string) => this.translate.translate(key);
    if (this.dateModel.range !== 'custom') {
      const key: Record<Exclude<DateRangeKind, 'custom'>, string> = {
        today: 'map.dateFilter.today',
        last24h: 'map.dateFilter.last24Hours',
        week: 'map.dateFilter.thisWeek',
        last7d: 'map.dateFilter.last7Days',
        month: 'map.dateFilter.thisMonth',
        last30d: 'map.dateFilter.last30Days',
      };
      this.activeRangeLabel.set(t(key[this.dateModel.range]));
      return;
    }
    const fmt = (v: string) =>
      v
        ? new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(v))
        : '…';
    this.activeRangeLabel.set(`${fmt(this.dateModel.from)} – ${fmt(this.dateModel.to)}`);
  }

  private initMap(): void {
    // MapLibre resolves its tile-decoding worker relative to import.meta.url, which
    // after bundling points at the app chunk, not node_modules -- the worker 404s and
    // no vector tiles ever load. The worker (+ its shared chunk) is copied to the site
    // root as an asset in angular.json; point MapLibre there explicitly.
    maplibregl.setWorkerUrl(new URL('maplibre-gl-worker.mjs', document.baseURI).href);

    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);

    // Set the initial camera from the saved view (or the NA fallback) directly, rather than
    // jumpTo-ing after 'load' -- that left the map flashing its 0,0 default center first. This
    // is safe unlike a `bounds`-on-construct option (see the resize/fit note below): fixed
    // center+zoom don't depend on the container's size, only a bounds *fit* does.
    const initialView = this.savedView ?? NORTH_AMERICA_VIEW;
    this.map = new maplibregl.Map({
      container: this.mapEl.nativeElement,
      // Hybrid needs an async fetch to build (see applyCurrentStyle) -- construct with
      // Streets as a placeholder for it and swap in the real style right after.
      style: this.mapStyleId() === 'satellite' ? buildSatelliteStyle() : STREETS_URL,
      center: initialView.center,
      zoom: initialView.zoom,
      bearing: this.savedView?.bearing ?? 0,
      pitch: this.savedView?.pitch ?? 0,
    });
    if (this.mapStyleId() === 'hybrid') {
      this.applyCurrentStyle();
    }

    this.map.addControl(new maplibregl.NavigationControl(), 'top-right');
    this.map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      'top-right',
    );
    this.map.addControl(new maplibregl.FullscreenControl(), 'top-right');
    this.map.addControl(new ElementControl(this.styleCtrl.nativeElement), 'top-right');
    this.map.addControl(new ElementControl(this.layersCtrl.nativeElement), 'top-right');
    this.map.addControl(new ElementControl(this.actionsCtrl.nativeElement), 'top-left');
    this.map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

    // setStyle() discards every non-style source/layer, so the registry overlay is
    // (re)built on each style.load rather than once on load.
    this.map.on('style.load', () => {
      this.addRegistryLayers();
      this.refreshPoiLayerIds();
    });

    this.map.on('load', async () => {
      // The container's flex layout may not have settled yet when the map was
      // constructed (ngAfterViewInit can fire before the browser's layout pass),
      // which left `bounds`-on-construct computing its fit against a 0-size
      // element and zooming in far past any real tile data. Resize + fit here
      // instead, once layout is guaranteed to be correct.
      this.map.resize();
      // Give the attribution its intended moment on screen (crediting Esri/OpenFreeMap/OSM)
      // before auto-collapsing it -- see collapseAttributionControl for why it can't just be
      // told to start collapsed.
      setTimeout(() => this.collapseAttributionControl(), 5000);
      // Camera is already set to the saved view (or the NA fallback) via the constructor
      // options above -- resize() here only accounts for layout settling, it doesn't need
      // to re-jump since fixed center+zoom (unlike a bounds fit) isn't size-dependent.
      this.map.on('moveend', () => this.saveViewState());
      this.map.on('movestart', () => this.selectedPoi.set(null));
      this.map.on('click', (e) => this.onMapTap(e));

      // Dev-only zoom readout in the app toolbar (see app.html, next to app-map-mode-toggle)
      // -- 'zoom' fires continuously during a zoom gesture, not just at the end, so the
      // readout tracks live while deciding tile zoom-range tuning.
      this.mapZoomService.zoom.set(this.map.getZoom());
      this.map.on('zoom', () => this.mapZoomService.zoom.set(this.map.getZoom()));

      // Bound once, before the building-fill handlers below, and using the same
      // by-reference poiLayerIds array that refreshPoiLayerIds() mutates in place on every
      // style.load -- registering first matters, since a click landing on a POI that sits
      // inside a building polygon fires every matching layer-scoped listener (MapLibre does
      // not stop at the first match), and onBuildingTap only bails out via the
      // e.defaultPrevented flag onPoiTap sets here if this listener ran first.
      this.map.on(
        'mouseenter',
        this.poiLayerIds,
        () => (this.map.getCanvas().style.cursor = 'pointer'),
      );
      this.map.on('mouseleave', this.poiLayerIds, () => (this.map.getCanvas().style.cursor = ''));
      this.map.on('click', this.poiLayerIds, (e) => this.onPoiTap(e));

      // 'pins-circle' itself is re-added on every style.load (addPinsLayer, called from
      // addRegistryLayers), but the id string is stable across that churn -- bind its
      // handlers once here, same as the poiLayerIds handlers just above.
      this.map.on('mouseenter', 'pins-circle', () => (this.map.getCanvas().style.cursor = 'pointer'));
      this.map.on('mouseleave', 'pins-circle', () => (this.map.getCanvas().style.cursor = ''));
      this.map.on('click', 'pins-circle', (e) => this.onPinTap(e));

      // Bound once (not per style.load, unlike addRegistryLayers) -- MapLibre resolves a
      // layer-filtered listener by id string at event time, so it keeps working across the
      // style.load-driven layer churn without needing to be re-registered.
      for (const region of await this.readyRegions) {
        this.bindRegionLayerHandlers(region.slug);
      }
    });

    this.darkModeQuery.addEventListener('change', () => this.recolorTheme());
  }

  /** MapLibre's default AttributionControl (a `<details>` element) forces itself open the
   *  first time it computes compact mode -- there's no constructor option to start collapsed,
   *  so it's stripped back to icon-only right after load. The `maplibregl-compact` class is
   *  left in place: removing it too would make the control think it hasn't gone compact yet,
   *  re-triggering the forced-open behavior on the next style change. */
  private collapseAttributionControl(): void {
    const attrib = this.mapEl.nativeElement.querySelector('.maplibregl-ctrl-attrib');
    attrib?.removeAttribute('open');
    attrib?.classList.remove('maplibregl-compact-show');
  }

  /** Recomputes which of the current style's layers carry POI features. `source-layer: 'poi'`
   *  is the stable OpenMapTiles schema name; the layer ids themselves (poi_r1, poi_r7, ...)
   *  belong to the fetched vendor style and could change if it's updated upstream. Mutating
   *  this array in place (rather than reassigning) is what lets the click/hover listeners
   *  bound once in 'load' pick up the change without being re-registered. */
  private refreshPoiLayerIds(): void {
    const ids = this.map
      .getStyle()
      .layers.filter((l) => 'source-layer' in l && l['source-layer'] === 'poi')
      .map((l) => l.id);
    this.poiLayerIds.length = 0;
    this.poiLayerIds.push(...ids);
  }

  private bindRegionLayerHandlers(slug: string): void {
    const fillLayer = `registry-fill-${slug}`;
    this.map.on('mouseenter', fillLayer, () => (this.map.getCanvas().style.cursor = 'pointer'));
    this.map.on('mouseleave', fillLayer, () => (this.map.getCanvas().style.cursor = ''));
    this.map.on('click', fillLayer, (e) => this.onBuildingTap(e, slug));

    // Coverage cells always carry feature_count > 1 (see backend's
    // buildCoverageFeatureCollection), so onBuildingTap's existing coalesced-group branch
    // handles a tap here with no extra logic -- it flies to a neighbourhood-scale view.
    const coverageFillLayer = `registry-coverage-fill-${slug}`;
    this.map.on('mouseenter', coverageFillLayer, () => (this.map.getCanvas().style.cursor = 'pointer'));
    this.map.on('mouseleave', coverageFillLayer, () => (this.map.getCanvas().style.cursor = ''));
    this.map.on('click', coverageFillLayer, (e) => this.onBuildingTap(e, slug));
  }

  /** Registry source/layer ids are keyed by region slug so a click handler and
   *  reloadOwnActivity's feature-state updates both know which region a feature belongs to
   *  without a separate "which region is this point in" lookup. */
  private async addRegistryLayers(): Promise<void> {
    // Registry pmtiles are served by the backend (see backend/src/routes/registry.ts), not
    // as an Angular build asset -- same host-derivation convention used for the /auth and
    // /sync-ws routes (environment.apiUrl.replace('/api', '')), since in dev the backend
    // runs on a different origin than the Angular dev server.
    const registryOrigin = environment.apiUrl.replace('/api', '');

    for (const region of await this.readyRegions) {
      const sourceId = `registry-${region.slug}`;
      this.map.addSource(sourceId, {
        type: 'vector',
        url: `pmtiles://${registryOrigin}/registry/${region.slug}.pmtiles`,
        promoteId: 'building_id',
      });

      const activityVisibility = this.activityLayerVisible() ? 'visible' : 'none';
      this.map.addLayer({
        id: `registry-fill-${region.slug}`,
        type: 'fill',
        source: sourceId,
        'source-layer': 'buildings',
        layout: { visibility: activityVisibility },
        paint: {
          'fill-color': ['coalesce', ['feature-state', 'color'], this.cssVar('--bg-tertiary')],
          'fill-opacity': 0.75,
        },
      });
      this.map.addLayer({
        id: `registry-line-${region.slug}`,
        type: 'line',
        source: sourceId,
        'source-layer': 'buildings',
        layout: { visibility: activityVisibility },
        paint: { 'line-color': this.cssVar('--border-primary-color'), 'line-width': 1 },
      });
      // The currently-selected address's building, if any -- kept as its own always-visible
      // layer (independent of the activityLayerVisible toggle above) rather than a feature-state
      // tweak on registry-line, since selection is orthogonal to the own-activity overlay and
      // should still read clearly with that overlay hidden. Filter starts matching nothing;
      // applySelectionFilter (called below, and on every route change) points it at the
      // selected building_id.
      this.map.addLayer({
        id: `registry-selected-${region.slug}`,
        type: 'line',
        source: sourceId,
        'source-layer': 'buildings',
        filter: ['==', ['get', 'building_id'], ''],
        paint: { 'line-color': this.cssVar('--border-accent-color'), 'line-width': 3 },
      });

      // Coverage: a separate low-zoom-only pmtiles archive (see backend's generate-tiles.ts
      // top comment) of solid, real convex-hull polygons -- unlike the buildings layer's own
      // --coalesce-smallest-as-needed tile-size workaround, these are genuinely unioned
      // shapes, so they're actually visible at low zoom instead of collapsing to sub-pixel
      // dots. `maxzoom: 15` hides this layer right where the buildings layer's own minzoom
      // (15) picks up, so the handoff between the two is a clean cut, not an overlap.
      // `promoteId: 'cell_id'` mirrors the buildings source's `promoteId: 'building_id'` --
      // reloadOwnActivity below sets feature-state color on a cell the same way it does per
      // building, recomputing the same grid cell (COVERAGE_GRID_DEGREES) for each locally-
      // active address.
      const coverageSourceId = `registry-coverage-${region.slug}`;
      this.map.addSource(coverageSourceId, {
        type: 'vector',
        url: `pmtiles://${registryOrigin}/registry/${region.slug}-coverage.pmtiles`,
        promoteId: 'cell_id',
      });
      this.map.addLayer({
        id: `registry-coverage-fill-${region.slug}`,
        type: 'fill',
        source: coverageSourceId,
        'source-layer': 'coverage',
        maxzoom: 15,
        layout: { visibility: activityVisibility },
        paint: {
          'fill-color': ['coalesce', ['feature-state', 'color'], this.cssVar('--bg-tertiary')],
          // Opacity itself is also feature-state-driven, not just color: a cell with activity
          // should read more solidly than a bare "there's data here" one -- both values are
          // user-configurable (see MapDisplaySettingsService, edited on the general settings
          // page), re-read fresh here since addRegistryLayers reruns on every style.load
          // (basemap switch).
          'fill-opacity': [
            'case',
            ['!=', ['feature-state', 'color'], null],
            this.displaySettings.activeRegionOpacity(),
            this.displaySettings.inactiveRegionOpacity(),
          ],
        },
      });
      this.map.addLayer({
        id: `registry-coverage-line-${region.slug}`,
        type: 'line',
        source: coverageSourceId,
        'source-layer': 'coverage',
        maxzoom: 15,
        layout: { visibility: activityVisibility },
        paint: { 'line-color': this.cssVar('--border-primary-color'), 'line-width': 1 },
      });
    }

    this.addPinsLayer();
    this.reloadOwnActivity();
    this.applySelectionFilter();
  }

  /** (Re)creates the 'pins' GeoJSON source/layer -- like the registry sources, wiped by
   *  setStyle() on a basemap switch, so this is called from addRegistryLayers on every
   *  style.load, not just once. Click/hover handlers are bound once elsewhere (see the
   *  'load' handler) since the 'pins-circle' layer id itself is stable across that churn.
   *  Painted from feature properties (not feature-state, unlike the registry fill layer) --
   *  this is a plain GeoJSON source rebuilt wholesale on every syncPinsSource() call, so
   *  there's no persistent per-feature id to key feature-state off. */
  private addPinsLayer(): void {
    this.map.addSource('pins', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    this.map.addLayer({
      id: 'pins-circle',
      type: 'circle',
      source: 'pins',
      paint: {
        'circle-radius': 8,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': this.cssVar('--bg-primary'),
      },
    });
    this.syncPinsSource(this.footprintlessAddresses());
  }

  /** Rebuilds the 'pins' source's data from the current footprint-less address list --
   *  called by the reactive effect on that query, and directly after addPinsLayer()
   *  recreates the source (the effect's dependency won't have changed then, so it wouldn't
   *  otherwise re-run) and after recolorTheme() (css var values change, not the address list). */
  private syncPinsSource(addresses: Address[]): void {
    const source = this.map?.getSource('pins') as maplibregl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: addresses
        .filter((a) => a.lat !== null && a.lng !== null)
        .map((a) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [a.lng as number, a.lat as number] },
          properties: {
            addressId: a.id,
            color: a.last_outcome_kind
              ? this.cssVar(
                  CHIP_COLOR_BG_VAR[outcomeStyle(a.last_outcome_kind, a.last_objection_id).color],
                )
              : this.cssVar('--bg-tertiary'),
          },
        })),
    });
  }

  private onPinTap(e: maplibregl.MapLayerMouseEvent): void {
    const addressId = e.features?.[0]?.properties?.['addressId'] as string | undefined;
    if (!addressId) return;
    this.addressService.getById(addressId).then((address) => {
      if (address) this.navigateToInteraction(address);
    });
  }

  // ---------- Map style toggle ----------

  /** Advances to the next style in the cycle -- bound to a plain click/tap. */
  cycleMapStyle(): void {
    this.mapStyleId.set(this.nextStyleId());
    this.applyCurrentStyle();
    this.saveViewState();
  }

  /** Picks a style directly from the flyout panel. */
  selectMapStyle(id: MapStyleId): void {
    this.showStylePicker.set(false);
    if (id === this.mapStyleId()) return;
    this.mapStyleId.set(id);
    this.applyCurrentStyle();
    this.saveViewState();
  }

  /** click fires on both a plain tap and the tap that ends a long-press -- when the long-press
   *  already opened the panel, this swallows that trailing click so it doesn't also cycle. */
  onStyleToggleClick(): void {
    if (this.suppressNextStyleClick) {
      this.suppressNextStyleClick = false;
      return;
    }
    this.cycleMapStyle();
  }

  onStyleCtrlMouseEnter(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.hoverTimer = setTimeout(() => this.showStylePicker.set(true), 500);
  }

  /** Closes on a short delay rather than immediately -- the button and panel abut with only a
   *  1px overlap (see .map-flyout-panel), so a fast/diagonal move from button to panel can
   *  still cross a frame where the pointer is briefly over neither; this grace period absorbs
   *  that without making a deliberate move-away feel laggy. Canceled by mouseenter above if the
   *  pointer lands back inside (e.g. on the panel, which is a DOM descendant of this control). */
  onStyleCtrlMouseLeave(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    this.closeTimer = setTimeout(() => this.showStylePicker.set(false), 250);
  }

  onStylePointerDown(e: PointerEvent): void {
    if (e.pointerType !== 'touch') return;
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      this.suppressNextStyleClick = true;
      this.showStylePicker.set(true);
    }, 500);
  }

  onStylePointerUp(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /** Applies the current style; hybrid is built asynchronously (fetches Streets to lift roads/labels from). */
  private applyCurrentStyle(): void {
    switch (this.mapStyleId()) {
      case 'streets':
        this.map.setStyle(STREETS_URL);
        break;
      case 'satellite':
        this.map.setStyle(buildSatelliteStyle());
        break;
      case 'hybrid':
        buildHybridStyle().then((style) => this.map.setStyle(style));
        break;
    }
  }

  // ---------- Layers ----------

  toggleLayersPanel(): void {
    this.layersOpen.update((v) => !v);
  }

  async toggleActivityLayer(): Promise<void> {
    this.activityLayerVisible.update((v) => !v);
    this.saveViewState();
    const visibility = this.activityLayerVisible() ? 'visible' : 'none';
    for (const region of await this.readyRegions) {
      const layerIds = [
        `registry-fill-${region.slug}`,
        `registry-line-${region.slug}`,
        `registry-coverage-fill-${region.slug}`,
        `registry-coverage-line-${region.slug}`,
      ];
      for (const id of layerIds) {
        if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', visibility);
      }
    }
  }

  private saveViewState(): void {
    const { lng, lat } = this.map.getCenter();
    this.userStorage.setTypedItem<MapViewState>(MAP_VIEW_KEY, {
      mapStyleId: this.mapStyleId(),
      activityLayerVisible: this.activityLayerVisible(),
      center: [lng, lat],
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
    });
  }

  private cssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  private async recolorTheme(): Promise<void> {
    for (const region of await this.readyRegions) {
      if (!this.map.getLayer(`registry-fill-${region.slug}`)) continue;
      this.map.setPaintProperty(`registry-fill-${region.slug}`, 'fill-color', [
        'coalesce',
        ['feature-state', 'color'],
        this.cssVar('--bg-tertiary'),
      ]);
      this.map.setPaintProperty(
        `registry-line-${region.slug}`,
        'line-color',
        this.cssVar('--border-primary-color'),
      );
      if (this.map.getLayer(`registry-selected-${region.slug}`)) {
        this.map.setPaintProperty(
          `registry-selected-${region.slug}`,
          'line-color',
          this.cssVar('--border-accent-color'),
        );
      }
      if (this.map.getLayer(`registry-coverage-fill-${region.slug}`)) {
        this.map.setPaintProperty(`registry-coverage-fill-${region.slug}`, 'fill-color', [
          'coalesce',
          ['feature-state', 'color'],
          this.cssVar('--bg-tertiary'),
        ]);
        this.map.setPaintProperty(
          `registry-coverage-line-${region.slug}`,
          'line-color',
          this.cssVar('--border-primary-color'),
        );
      }
    }
    this.reloadOwnActivity(); // feature-state colors were resolved from the old theme too
    this.syncPinsSource(this.footprintlessAddresses()); // pins' colors are also resolved eagerly
  }

  // ---------- Date filter / own-activity layer ----------

  private dateRangeBounds(): { from: number; to: number } {
    const now = new Date(this.clockSync.now());
    if (this.dateModel.range === 'custom') {
      const from = this.dateModel.from ? new Date(this.dateModel.from).getTime() : 0;
      const to = this.dateModel.to ? new Date(this.dateModel.to).getTime() : this.clockSync.now();
      return { from, to };
    }
    // "Last N hours/days" round to the current hour/day boundary rather than counting back
    // N full units from the exact current instant -- otherwise the range creeps into an extra
    // unit as the day goes on (e.g. "last 7 days" checked late in the day would span parts of
    // 8 calendar days). Rounding keeps the boundary at N-1 full prior units plus the partial
    // current one, so "last 7 days" is really ~6.xxx days ending now.
    if (this.dateModel.range === 'last24h') {
      const hourStart = new Date(now);
      hourStart.setMinutes(0, 0, 0);
      hourStart.setHours(hourStart.getHours() - 23);
      return { from: hourStart.getTime(), to: this.clockSync.now() };
    }
    if (this.dateModel.range === 'last7d' || this.dateModel.range === 'last30d') {
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      dayStart.setDate(dayStart.getDate() - (this.dateModel.range === 'last7d' ? 6 : 29));
      return { from: dayStart.getTime(), to: this.clockSync.now() };
    }

    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    if (this.dateModel.range === 'week') start.setDate(start.getDate() - start.getDay());
    if (this.dateModel.range === 'month') start.setDate(1);
    return { from: start.getTime(), to: this.clockSync.now() };
  }

  private async reloadOwnActivity(): Promise<void> {
    const regions = (await this.readyRegions).filter((r) =>
      this.map?.getSource(`registry-${r.slug}`),
    );
    if (regions.length === 0) return;
    const userId = this.authService.getCurrentUserId();
    if (!userId) return;

    const { from, to } = this.dateRangeBounds();
    const interactions = await this.interactionService.getForUserBetween(userId, from, to);

    // Latest interaction per address wins the colour, matching the address list/detail
    // pages' own "most recent outcome" convention.
    const latestByAddress = new Map<string, Interaction>();
    for (const i of interactions) {
      const existing = latestByAddress.get(i.address_id);
      if (!existing || i.occurred_at > existing.occurred_at) latestByAddress.set(i.address_id, i);
    }

    // Clear previous feature-state before applying the new set, so an address that drops
    // out of the range (e.g. switching from "this week" to "today") reverts to unmatched-grey.
    for (const region of regions) {
      this.map.removeFeatureState({ source: `registry-${region.slug}`, sourceLayer: 'buildings' });
      this.map.removeFeatureState({ source: `registry-coverage-${region.slug}`, sourceLayer: 'coverage' });
    }

    for (const interaction of latestByAddress.values()) {
      const address = await this.addressService.getById(interaction.address_id);
      if (!address?.building_id) continue; // manually-entered address with no map location
      const { color } = outcomeStyle(interaction.outcome_kind);
      // An address's local record doesn't currently say which region its building came
      // from (see map.component.ts's design note in the plan), so set the feature-state on
      // every loaded region's source -- MapLibre just stores state keyed by id per source
      // and only renders it where a matching feature actually exists, so this is harmless
      // for the N-1 regions that don't have this building_id.
      for (const region of regions) {
        this.map.setFeatureState(
          { source: `registry-${region.slug}`, sourceLayer: 'buildings', id: address.building_id },
          { color: this.cssVar(CHIP_COLOR_BG_VAR[color]) },
        );
        // Also flag the coverage cell this address falls in, if any (only populated once a
        // region's coverage layer is loaded -- see addRegistryLayers). Deliberately a single
        // fixed, user-configurable color (MapDisplaySettingsService), not the per-outcome
        // color used for an individual building above: a cell aggregates many buildings that
        // could carry several different outcomes, so it only signals "there was activity
        // somewhere in here" -- not "latest wins" the way per-building color is, just
        // whichever address in the cell happened to be processed last.
        if (address.lat != null && address.lng != null) {
          this.map.setFeatureState(
            { source: `registry-coverage-${region.slug}`, sourceLayer: 'coverage', id: coverageCellId(address.lng, address.lat) },
            { color: this.displaySettings.activeRegionColor() },
          );
        }
      }
    }
  }

  // ---------- Selected-address highlight ----------

  /** Reads the ':id' param off whichever /map/addresses/:id/... child route is currently
   *  active -- every such route (detail, edit, interaction, lead wizard, objection) nests
   *  under that one param (see app.routes.ts), so the deepest 'id' found while walking down the
   *  active route tree is the address currently on screen. Walking from this.route (MapComponent
   *  owns the outlet all of these render into) rather than router.routerState.root, since it's
   *  already scoped to the /map subtree. */
  private resolveSelectedAddressIdFromRoute(): string | null {
    let snapshot: ActivatedRouteSnapshot | null = this.route.snapshot;
    let addressId: string | null = null;
    while (snapshot) {
      addressId = snapshot.paramMap.get('id') ?? addressId;
      snapshot = snapshot.firstChild;
    }
    return addressId;
  }

  /** Fires on every /map/* navigation, including returning here from a child route (interaction
   *  screen, objection panel, lead wizard) after a save -- MapComponent owns the outlet those
   *  render into, so it's never destroyed/recreated by that navigation and reloadOwnActivity's
   *  other triggers (initial load, date-filter change, theme change) don't cover it. Re-running
   *  it here is what makes a just-logged interaction recolor its building immediately instead of
   *  only on the next full remount. */
  private onNavigationEnd(): void {
    this.updateSelectedAddressFromRoute();
    this.reloadOwnActivity();
  }

  private async updateSelectedAddressFromRoute(): Promise<void> {
    const id = this.resolveSelectedAddressIdFromRoute();
    this.selectedAddressId.set(id);
    const address = id ? await this.addressService.getById(id) : null;
    this.selectedBuildingId = address?.building_id ?? null; // a manually-dropped pin has none -- no polygon to highlight
    await this.applySelectionFilter();
  }

  /** Points the (always-present, initially match-nothing) registry-selected-* layers at the
   *  selected building_id, or back at match-nothing when there isn't one. Re-run here and after
   *  every addRegistryLayers() rebuild (basemap switch), same as reloadOwnActivity's feature-state. */
  private async applySelectionFilter(): Promise<void> {
    if (!this.map) return;
    const filter: maplibregl.FilterSpecification = [
      '==',
      ['get', 'building_id'],
      this.selectedBuildingId ?? '',
    ];
    for (const region of await this.readyRegions) {
      const layerId = `registry-selected-${region.slug}`;
      if (this.map.getLayer(layerId)) this.map.setFilter(layerId, filter);
    }
  }

  // ---------- Tap a building ----------

  private async onBuildingTap(e: maplibregl.MapLayerMouseEvent, regionSlug: string): Promise<void> {
    if (this.dropPinMode()) return; // pin-drop mode is handled by the generic map click below
    if (e.defaultPrevented) return; // a POI under the same point already handled this click
    const feature = e.features?.[0];

    // feature_count (summed via --accumulate-attribute, see generate-tiles.ts) is how many
    // real buildings a tapped tile feature represents -- > 1 means either a coverage cell
    // (source-layer 'coverage') or a buildings-layer group tippecanoe's
    // --coalesce-smallest-as-needed merged to stay under the tile size limit. Either way, a
    // building_id/unit lookup on it would show wrong/incomplete data (arbitrarily from just
    // one of the merged buildings), so zoom in instead of resolving it.
    const featureCount = feature?.properties?.['feature_count'] as number | undefined;
    if (featureCount && featureCount > 1) {
      if (feature?.sourceLayer === 'coverage' && feature.geometry) {
        // Coverage cells are real solid ST_ConvexHull polygons (see backend's
        // buildCoverageFeatureCollection) -- a genuine spatial boundary, so fitBounds() on
        // the tapped feature's own geometry is meaningful: it zooms to the region's actual
        // extent, not just a point. But a wide cell (more buildings spread further apart)
        // can naturally fit at a zoom shallower than TAP_MIN_ZOOM -- below that the buildings
        // layer (minzoom 15) still isn't visible, so the tap wouldn't feel like it "drilled
        // in" at all. cameraForBounds computes the camera fitBounds would use WITHOUT moving
        // the map, so that case can be caught and overridden with a flyTo to a guaranteed
        // TAP_MIN_ZOOM instead, centered on the same bounds.
        const bounds = this.boundsFromGeometry(feature.geometry);
        const camera = this.map.cameraForBounds(bounds, { padding: 40 });
        if (camera?.zoom != null && camera.zoom < TAP_MIN_ZOOM) {
          this.map.flyTo({ center: camera.center, zoom: TAP_MIN_ZOOM, duration: 1000 });
        } else {
          this.map.fitBounds(bounds, { padding: 40 });
        }
      } else {
        // Buildings-layer coalescing only bins nearby small polygons into one tile feature
        // for size purposes, it does NOT spatially union them into one blob -- a merge can
        // be just a couple of adjacent building footprints, so fitBounds() on it would zoom
        // in tight enough to reveal only a single building. Fly to a fixed neighbourhood-
        // scale zoom instead.
        this.map.flyTo({ center: [e.lngLat.lng, e.lngLat.lat], zoom: TAP_MIN_ZOOM, duration: 1000 });
      }
      return;
    }

    const buildingId = feature?.properties?.['building_id'] as string | undefined;
    if (!buildingId) return;

    this.loadingTap.set(true);
    try {
      const [units, localAddresses] = await Promise.all([
        this.registryLookup.lookupUnits(regionSlug, buildingId, e.lngLat.lng, e.lngLat.lat),
        this.addressService.getAllByBuildingId(buildingId),
      ]);

      if (units.length === 0) {
        if (localAddresses.length > 0) {
          // Already auto-resolved on a previous tap -- go straight there instead of
          // re-running a registry lookup that will find nothing all over again.
          this.navigateToInteraction(localAddresses[0]);
          return;
        }
        // The registry has no known unit for this building -- auto-resolve by reverse
        // geocoding the tapped location instead of making the user drop a pin by hand.
        await this.createAndResolveAddress(e.lngLat.lat, e.lngLat.lng, buildingId);
        return;
      }

      if (units.length === 1) {
        await this.resolveAndNavigate(units[0], localAddresses[0] ?? null);
        return;
      }

      const localByRegistryId = new Map(localAddresses.map((a) => [a.registry_id, a]));
      this.pickerRows.set(
        units.map((u) => ({ registryUnit: u, localAddress: localByRegistryId.get(u.id) ?? null })),
      );
      this.pickerBuildingId.set(buildingId);
      this.showUnitPicker.set(true);
    } catch (err) {
      this.log.error('Failed to resolve tapped building', err);
      this.toastService.error(this.translate.translate('map.error.lookupFailed'));
    } finally {
      this.loadingTap.set(false);
    }
  }

  /** Walks a GeoJSON geometry's coordinate tree (works for Polygon and MultiPolygon alike,
   *  which is what a coverage cell's ST_ConvexHull can be) to build the bounds fitBounds()
   *  needs. */
  private boundsFromGeometry(geometry: maplibregl.MapGeoJSONFeature['geometry']): maplibregl.LngLatBounds {
    const bounds = new maplibregl.LngLatBounds();
    const extend = (node: unknown): void => {
      if (Array.isArray(node)) {
        if (typeof node[0] === 'number' && typeof node[1] === 'number') {
          bounds.extend(node as [number, number]);
        } else {
          node.forEach(extend);
        }
      }
    };
    if ('coordinates' in geometry) extend(geometry.coordinates);
    return bounds;
  }

  async pickUnit(row: UnitPickerRow): Promise<void> {
    this.showUnitPicker.set(false);
    this.loadingTap.set(true);
    try {
      await this.resolveAndNavigate(row.registryUnit, row.localAddress);
    } finally {
      this.loadingTap.set(false);
    }
  }

  private async resolveAndNavigate(unit: RegistryUnit, existing: Address | null): Promise<void> {
    if (existing) {
      this.navigateToInteraction(existing);
      return;
    }
    const created = await this.addressService.create(this.registryUnitToCreateParams(unit));
    this.navigateToInteraction(created);
  }

  /** Navigates to the interaction screen (or, in browse mode, the address's history)
   *  for a resolved/created address -- the do-not-contact confirm gate (design doc
   *  §6.3 supersession: one upfront, outcome-independent confirm instead of a
   *  per-outcome override at save time) lives in the interaction screen itself,
   *  since deep links (e.g. from Address Detail's timeline) can land there without
   *  passing through the map at all. */
  private navigateToInteraction(address: Address): void {
    if (this.mapModeService.mode() === 'browse') {
      this.router.navigate(['/map/addresses', address.id]);
      return;
    }
    this.router.navigate(['/map/addresses', address.id, 'interactions', 'new']);
  }

  private registryUnitToCreateParams(unit: RegistryUnit): CreateAddressParams {
    return {
      formatted_address: unit.formatted_address,
      unit: unit.unit,
      house_number: unit.house_number,
      street: unit.street,
      city: unit.city,
      region: unit.region,
      postal_code: unit.postal_code,
      country: unit.country,
      place_type: (unit.place_type as CreateAddressParams['place_type']) ?? 'unknown',
      lat: unit.lat,
      lng: unit.lng,
      geocode_status: 'resolved',
      registry_id: unit.id,
      building_id: unit.building_id,
    };
  }

  // ---------- Activity filter ----------

  toggleActivityFilter(): void {
    this.activityFilterOpen.update((v) => !v);
  }

  closeActivityFilter(): void {
    this.activityFilterOpen.set(false);
  }

  // ---------- Map search ----------

  onSearchInput(query: string): void {
    this.searchQuery.set(query);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (!query.trim()) {
      this.searchResults.set([]);
      this.showSearchResults.set(false);
      return;
    }

    this.searchDebounceTimer = setTimeout(() => this.performSearch(query), 300);
  }

  /**
   * `no_match` (a real 200 with zero results) is an expected, unremarkable outcome -- shown
   * as the dropdown's own inline empty state (see template's search-results-empty branch),
   * not a toast. Only `network` (and a thrown exception, which shouldn't happen here but would
   * indicate something actually wrong) is worth interrupting the user for.
   */
  private async performSearch(query: string): Promise<void> {
    this.searchLoading.set(true);
    try {
      const center = this.map?.getCenter();
      const result = await this.geocodingService.forwardGeocode(query, center?.lat, center?.lng);
      if (result.success) {
        this.searchResults.set(
          result.results.map((r) => ({
            address: r.formattedAddress,
            lat: r.lat,
            lng: r.lng,
          })),
        );
        this.showSearchResults.set(true);
      } else {
        this.searchResults.set([]);
        this.showSearchResults.set(true);
        if (result.reason === 'network') {
          this.toastService.error(this.translate.translate('map.search.error'));
        }
      }
    } catch (err) {
      this.log.error('Address search failed', err);
      this.searchResults.set([]);
      this.showSearchResults.set(true);
      this.toastService.error(this.translate.translate('map.search.error'));
    } finally {
      this.searchLoading.set(false);
    }
  }

  /** A search result is often not a real address at all (a street crossroad, a place name),
   *  and even when it is, the canvasser may just be looking. So selecting one marks nothing
   *  but the location itself -- no address is created, nothing is navigated to. Just the map
   *  moving there and a marker the user can inspect (address text) or dismiss (delete icon).
   *  Kept independent of onMapTap's dropPinMode flow, an explicit "drop a pin here" gesture
   *  that still commits immediately to creating an address. */
  selectSearchResult(result: { address: string; lat: number; lng: number }): void {
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.showSearchResults.set(false);

    this.map.flyTo({ center: [result.lng, result.lat], zoom: 16, duration: 1000 });
    this.showSearchPin(result.lat, result.lng, result.address);
  }

  /** Places (replacing any previous one -- only one search pin exists at a time) a marker at
   *  a search result's location. A plain DOM `maplibregl.Marker`, not the 'pins' GeoJSON layer
   *  used for committed footprint-less addresses, since markers aren't wiped by setStyle() on
   *  a basemap switch and this is a single transient point, not a reactively-synced collection.
   *  Its attached Popup (MapLibre toggles it automatically on marker click) shows the address
   *  and a delete icon -- the only actions available, since the pin represents nothing but the
   *  searched location itself. */
  private showSearchPin(lat: number, lng: number, address: string): void {
    this.clearSearchPin();
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'search-pin-marker';
    el.setAttribute('data-testid', 'search-pin-marker');

    const popupEl = document.createElement('div');
    popupEl.className = 'search-pin-popup';
    const addressEl = document.createElement('div');
    addressEl.className = 'search-pin-popup-address';
    addressEl.textContent = address;
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'search-pin-popup-delete';
    deleteBtn.setAttribute('data-testid', 'search-pin-delete');
    deleteBtn.setAttribute('aria-label', this.translate.translate('map.search.deletePin'));
    deleteBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/>' +
      '<path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>' +
      '<path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>' +
      '<line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>';
    deleteBtn.addEventListener('click', () => this.clearSearchPin());
    popupEl.append(addressEl, deleteBtn);

    const popup = new maplibregl.Popup({ closeButton: false, offset: 20 }).setDOMContent(popupEl);
    this.searchPinMarker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([lng, lat])
      .setPopup(popup)
      .addTo(this.map);
  }

  private clearSearchPin(): void {
    this.searchPinMarker?.remove();
    this.searchPinMarker = null;
  }

  clearSearch(): void {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.showSearchResults.set(false);
  }

  // ---------- Pin drop ----------

  toggleDropPin(): void {
    this.dropPinMode.update((v) => !v);
  }

  private async onMapTap(e: maplibregl.MapMouseEvent): Promise<void> {
    this.selectedPoi.set(null); // every click closes an open POI popup; onPoiTap below reopens it if this click hit one
    if (!this.dropPinMode()) return;
    this.dropPinMode.set(false);
    this.loadingTap.set(true);
    try {
      await this.createAndResolveAddress(e.lngLat.lat, e.lngLat.lng);
    } finally {
      this.loadingTap.set(false);
    }
  }

  /**
   * Creates an address at a tapped location and resolves it in the background via reverse
   * geocoding -- shared by manual pin-drop and by onBuildingTap's auto-resolve path for a
   * building the registry has no unit data for. Never blocks navigation on the network:
   * the address is created immediately with a placeholder, then filled in once the reverse
   * geocode returns. See design doc §9.3.
   */
  private async createAndResolveAddress(
    lat: number,
    lng: number,
    buildingId?: string,
  ): Promise<void> {
    try {
      const created = await this.addressService.create({
        formatted_address: this.translate.translate('map.pinPlaceholder', {
          lat: lat.toFixed(5),
          lng: lng.toFixed(5),
        }),
        lat,
        lng,
        geocode_status: 'pending',
        ...(buildingId ? { building_id: buildingId } : {}),
      });

      this.navigateToInteraction(created);

      const geocoded = await this.geocodingService.reverseGeocode(lat, lng);
      if (geocoded.success) {
        await this.addressService.update({ id: created.id, ...geocoded.address });
        // is_locally_edited stays 0 here since this came from AddressService.update()'s
        // normal path -- acceptable: a freshly resolved address has no local correction
        // yet to protect from a future registry projection.
      } else {
        this.log.info('Reverse geocode did not resolve', { reason: geocoded.reason });
      }
    } catch (err) {
      this.log.error('Failed to create address for tapped location', err);
      this.toastService.error(this.translate.translate('map.error.createAddressFailed'));
    }
  }

  cancelUnitPicker(): void {
    this.showUnitPicker.set(false);
  }

  // ---------- Tap a basemap POI ----------

  private onPoiTap(e: maplibregl.MapLayerMouseEvent): void {
    const feature = e.features?.[0];
    if (!feature) return;
    // Must run before onBuildingTap's delegate for the same click (see the 'load' handler's
    // registration-order comment) so its e.defaultPrevented guard sees this.
    e.preventDefault();
    const props = feature.properties ?? {};
    this.selectedPoi.set({
      name: (props['name_en'] as string) || (props['name'] as string) || null,
      poiClass: (props['class'] as string) ?? null,
      subclass: (props['subclass'] as string) ?? null,
      point: e.point,
    });
  }

  closePoiPopup(): void {
    this.selectedPoi.set(null);
  }

  poiIcon(poiClass: string | null, subclass: string | null): string {
    return (
      (subclass && POI_CLASS_ICONS[subclass]) ||
      (poiClass && POI_CLASS_ICONS[poiClass]) ||
      'map-pin'
    );
  }
}
